import { useMemo } from 'react';
import { ChartDataItem, ChartLegendItem, ChartSeriesItem } from './types';
import { getWeekdayLabels } from './utils';

export { DEFAULT_WEEKDAY_LABELS } from './utils';

export type UseStackedBarChartProps = {
  data?: ChartDataItem[];
  series?: (ChartSeriesItem & { percentage?: number })[];
  legendSeries?: (ChartSeriesItem & { percentage?: number })[];
  xKey?: string;
  xLabels?: string[];
  useWeekdayLabels?: boolean;
  valueScale?: number;
  t?: (key: string) => string;
};

export const getNiceMax = (max: number): number => {
  if (max <= 0) return 50; // fallback default for empty charts
  const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
  const normalized = max / magnitude;
  const nice = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * magnitude;
};

export const useStackedBarChart = ({
  data = [],
  series = [],
  legendSeries,
  xKey = 'day',
  xLabels,
  useWeekdayLabels,
  valueScale = 1,
  t,
}: UseStackedBarChartProps) => {
  const weekdayLabels = useMemo(() => {
    if (xLabels && xLabels.length > 0) return xLabels;
    return getWeekdayLabels(t);
  }, [xLabels, t]);

  const chartData = useMemo(() => {
    // If useWeekdayLabels is explicitly false, use data's xKey directly without mapping to weekdays
    const shouldApplyWeekdayLabels =
      useWeekdayLabels === true || (useWeekdayLabels !== false && data.length === 7 && xKey === 'day' && !xLabels);

    return data.map((item, index) => {
      let xValue = item[xKey];
      if (xLabels && xLabels[index] !== undefined) {
        xValue = xLabels[index];
      } else if (shouldApplyWeekdayLabels && index < weekdayLabels.length) {
        xValue = weekdayLabels[index];
      }

      const row: ChartDataItem = {
        ...item,
        [xKey]: xValue ?? `Item ${index + 1}`,
      };

      series.forEach((s) => {
        const raw = item[s.key];
        const num = typeof raw === 'number' ? raw / valueScale : Number(raw) / valueScale || 0;
        row[s.key] = num;
      });

      return row;
    });
  }, [data, series, xKey, weekdayLabels, xLabels, useWeekdayLabels, valueScale]);

  const hasData = useMemo(() => {
    return chartData.some((item) => series.some((s) => Number(item[s.key] || 0) > 0));
  }, [chartData, series]);

  const totalsBySeries = useMemo(() => {
    return series.reduce<Record<string, number>>((acc, s) => {
      acc[s.key] = chartData.reduce((sum, item) => sum + Number(item[s.key] || 0), 0);
      return acc;
    }, {});
  }, [chartData, series]);

  const grandTotal = useMemo(() => {
    return Object.values(totalsBySeries).reduce((a, b) => a + b, 0);
  }, [totalsBySeries]);

  const legendItems = useMemo<ChartLegendItem[]>(() => {
    const targetSeries = legendSeries && legendSeries.length > 0 ? legendSeries : series;
    return targetSeries.map((s) => {
      const total = totalsBySeries[s.key] ?? 0;
      const calculatedPercentage = grandTotal > 0 ? Math.round((total / grandTotal) * 100) : 0;
      const percentage = s.percentage !== undefined ? s.percentage : calculatedPercentage;
      return {
        key: s.key,
        name: s.name,
        color: s.color,
        total,
        percentage,
      };
    });
  }, [series, legendSeries, totalsBySeries, grandTotal]);

  const maxStackedValue = useMemo(() => {
    if (chartData.length === 0) return 0;
    return Math.max(0, ...chartData.map((item) => series.reduce((sum, s) => sum + Number(item[s.key] || 0), 0)));
  }, [chartData, series]);

  const niceMax = useMemo(() => getNiceMax(maxStackedValue), [maxStackedValue]);

  const dynamicTicks = useMemo(() => {
    return Array.from({ length: 6 }, (_, i) => Math.round((niceMax / 5) * i));
  }, [niceMax]);

  return {
    chartData,
    hasData,
    totalsBySeries,
    grandTotal,
    legendItems,
    niceMax,
    dynamicTicks,
  };
};
